import { useCallback, useEffect, useState } from "react";
import { useSession } from "../hooks";
import type { Workflow, WorkflowRun } from "../sync/types";
import { WorkflowForm } from "./WorkflowForm";
import { Badge, Card, EmptyState, Field, Page } from "./ui";
import { locale, t } from "../i18n";

const example = {
  name: "hello",
  trigger: { type: "manual" },
  steps: [{ id: "say", type: "message", channel_id: "<channel id>", text: "Hello from {{workflow.name}}" }],
};

// The words for a trigger's and a step's type, and a run's or step's state,
// where they are shown; any other stays as it is.
const kindLabel = (k: string): string =>
  ({
    manual: t("manual"),
    shortcut: t("shortcut"),
    command: t("command"),
    message: t("message"),
    reaction: t("reaction"),
    schedule: t("schedule"),
    webhook: t("webhook"),
    task: t("task"),
    approval: t("approval"),
    form: t("form"),
    branch: t("branch"),
    create_channel: t("create_channel"),
    invite: t("invite"),
  })[k] ?? k;

const stateLabel = (st: string): string =>
  ({
    running: t("running"),
    waiting: t("waiting"),
    retrying: t("retrying"),
    completed: t("completed"),
    failed: t("failed"),
    cancelled: t("cancelled"),
  })[st] ?? st;

// WorkflowsView lets admins build workflows in a form or write them as JSON
// (docs/specs/workflows.md), run them by hand, and follow each run's steps.
export function WorkflowsView() {
  const session = useSession();
  const [list, setList] = useState<Workflow[] | null>(null);
  const [selected, setSelected] = useState<Workflow | "new" | null>(null);
  const [draft, setDraft] = useState("");
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [run, setRun] = useState<WorkflowRun | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [canDescribe, setCanDescribe] = useState(false);
  const [description, setDescription] = useState("");
  const [writing, setWriting] = useState(false);
  const [mode, setMode] = useState<"form" | "json">("form");
  useEffect(() => {
    void session.features().then((f) => setCanDescribe(!!f.large));
  }, [session]);

  // describe has the model write a definition into the editor, to review
  // and save.
  async function describe() {
    setWriting(true);
    setError("");
    try {
      const { definition } = await session.api.draftWorkflow(description);
      setDraft(JSON.stringify(definition, null, 2));
      setNotice(t("Written from your description. Check it, then save."));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWriting(false);
    }
  }

  const load = useCallback(async () => {
    try {
      setList((await session.api.workflows()).workflows);
    } catch (err) {
      setError(String(err));
    }
  }, [session]);
  useEffect(() => void load(), [load]);

  const loadRuns = useCallback(
    async (id: string) => {
      setRuns((await session.api.runs(id).catch(() => ({ runs: [] }))).runs);
    },
    [session],
  );
  useEffect(() => {
    if (!selected || selected === "new") return;
    void loadRuns(selected.id);
    const tick = setInterval(() => void loadRuns(selected.id), 5000);
    return () => clearInterval(tick);
  }, [selected, loadRuns]);

  const open = (w: Workflow | "new") => {
    setSelected(w);
    setRun(null);
    setNotice("");
    setError("");
    setDraft(JSON.stringify(w === "new" ? example : w.definition, null, 2));
  };

  async function save() {
    setError("");
    setNotice("");
    let def: unknown;
    try {
      def = JSON.parse(draft);
    } catch {
      setError(t("The definition is not valid JSON."));
      return;
    }
    try {
      if (selected === "new") {
        const r = await session.api.createWorkflow(def);
        setSelected(r.workflow);
        setNotice(r.webhook_url ? t("Created. Its webhook URL, shown only now: {url}", { url: r.webhook_url }) : t("Created. Invite its agent to the channels it posts in."));
      } else if (selected) {
        setSelected((await session.api.updateWorkflow(selected.id, def)).workflow);
        setNotice(t("Saved a new version."));
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function start() {
    if (!selected || selected === "new") return;
    try {
      const { run } = await session.api.startRun(selected.id);
      setNotice(t("Started run {id}.", { id: run.id.slice(-8) }));
      await loadRuns(selected.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function showRun(id: string) {
    setRun((await session.api.run(id)).run);
  }

  const runTone = (st: string) => (st === "completed" ? "ok" : st === "failed" ? "bad" : st === "cancelled" ? "neutral" : "accent");

  return (
    <Page
      className="workflows-view"
      title={t("Workflows")}
      description={t("Automations that run on their own: a trigger, then steps that post, ask people, hand work to agents or call your systems.")}
      actions={
        <button className="primary" onClick={() => open("new")}>
          {t("New workflow")}
        </button>
      }
    >
      {error && <p className="error">{error}</p>}
      {notice && <p className="notice">{notice}</p>}
      <div className="split workflows-layout">
        <Card title={t("All workflows")} className="workflow-list-card">
          {list?.length === 0 ? (
            <EmptyState icon="⚙️" title={t("No workflows yet")}>
              {t("Create one to automate approvals, hand-offs or alerts.")}
            </EmptyState>
          ) : (
            <ul className="rows workflow-list">
              {list?.map((w) => (
                <li key={w.id} className={selected !== "new" && selected?.id === w.id ? "active" : ""}>
                  <button className="workflow-item" onClick={() => open(w)}>
                    <span className="row-title">{w.name}</span>
                    <span className="row-meta">
                      <Badge tone={w.enabled ? "accent" : "neutral"}>{kindLabel(w.definition.trigger.type)}</Badge> v{w.version}
                      {!w.enabled && ` · ${t("off")}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
        {!selected ? (
          <Card>
            <EmptyState icon="🧭" title={t("Pick a workflow, or start a new one")}>
              {t("Build it in the form, write the JSON, or, with a model, describe it in a sentence.")}
            </EmptyState>
          </Card>
        ) : (
          <div className="workflow-editor">
            <Card
              title={selected === "new" ? t("New workflow") : selected.name}
              description={
                selected !== "new"
                  ? t("Acts as {name} · version {version}", { name: session.name(selected.agent_id), version: selected.version })
                  : t("Build it in the form or as JSON, then create it.")
              }
              actions={
                <div className="segmented editor-mode" role="tablist">
                  <button role="tab" aria-selected={mode === "form"} className={mode === "form" ? "active" : ""} onClick={() => setMode("form")}>
                    {t("Form")}
                  </button>
                  <button role="tab" aria-selected={mode === "json"} className={mode === "json" ? "active" : ""} onClick={() => setMode("json")}>
                    JSON
                  </button>
                </div>
              }
            >
              {selected === "new" && canDescribe && (
                <div className="describe">
                  <Field label={t("Describe it")} hint={t("The model writes a definition for you to check before saving.")}>
                    <textarea
                      aria-label={t("Describe the workflow")}
                      placeholder={t("When a message in #deploys says deploy <service>, ask the release manager to approve…")}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                    />
                  </Field>
                  <div className="form-actions">
                    <button onClick={describe} disabled={writing || !description.trim()}>
                      {writing ? t("Writing…") : t("Write it")}
                    </button>
                  </div>
                </div>
              )}
              {mode === "json" ? (
                <textarea className="definition" aria-label={t("Definition")} value={draft} onChange={(e) => setDraft(e.target.value)} spellCheck={false} />
              ) : (
                (() => {
                  try {
                    const def = JSON.parse(draft) as Record<string, unknown>;
                    return <WorkflowForm value={def} onChange={(d) => setDraft(JSON.stringify(d, null, 2))} />;
                  } catch {
                    return <p className="error">{t("The JSON has a mistake; fix it on the JSON tab.")}</p>;
                  }
                })()
              )}
              <div className="form-actions actions">
                {selected !== "new" && (
                  <button className="secondary" onClick={start}>
                    {t("Run now")}
                  </button>
                )}
                <button className="primary" onClick={save}>
                  {selected === "new" ? t("Create") : t("Save")}
                </button>
              </div>
            </Card>
            {selected !== "new" && (
              <Card title={t("Recent runs")} description={t("Every run keeps a trace of its steps; open one to see it.")}>
                {runs.length === 0 ? (
                  <EmptyState icon="▶️" title={t("No runs yet")}>
                    {t("Run it now, or wait for its trigger.")}
                  </EmptyState>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>{t("Started")}</th>
                          <th>{t("State")}</th>
                          <th>{t("Problem")}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map((r) => (
                          <tr key={r.id} className={`run-${r.state}`}>
                            <td>
                              <button className="link" onClick={() => showRun(r.id)}>
                                {new Date(r.created_at).toLocaleString(locale)}
                              </button>
                            </td>
                            <td>
                              <Badge tone={runTone(r.state)}>{stateLabel(r.state)}</Badge>
                            </td>
                            <td className="muted">{r.error ? (r.error.step ? t("{code} at {step}", { code: r.error.code, step: r.error.step }) : r.error.code) : "—"}</td>
                            <td className="num">
                              {(r.state === "running" || r.state === "waiting") && (
                                <button className="small" onClick={() => session.api.cancelRun(r.id).then(() => loadRuns(r.workflow_id))}>
                                  {t("Cancel")}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
            {selected !== "new" && run && (
              <Card title={t("Run {id}", { id: run.id.slice(-8) })} className="run-trace" actions={<Badge tone={runTone(run.state)}>{stateLabel(run.state)}</Badge>}>
                <ol className="timeline">
                  {run.trace?.map((s) => (
                    <li key={s.seq} className={`step-${s.state}`}>
                      <span className={`check-icon ${s.state === "completed" ? "ok" : s.state === "failed" ? "fail" : "warn"}`} aria-hidden>
                        {s.state === "completed" ? "✓" : s.state === "failed" ? "✕" : "…"}
                      </span>
                      <div className="row-main">
                        <span className="row-title">{s.step_id}</span>
                        <span className="row-meta">
                          {kindLabel(s.type)} · {stateLabel(s.state)}
                          {s.attempt > 1 && ` · ${t("attempt {n}", { n: s.attempt })}`}
                        </span>
                        {s.output != null && <pre>{JSON.stringify(s.output, null, 2)}</pre>}
                        {s.error != null && <pre className="error">{JSON.stringify(s.error, null, 2)}</pre>}
                      </div>
                    </li>
                  ))}
                </ol>
              </Card>
            )}
          </div>
        )}
      </div>
    </Page>
  );
}
